import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import usePageTitle from "../helpers/usePageTitle";
import { useModal } from "../components/modal/ModalContext";
import {
  getLeadListsPromise,
  deleteLeadListPromise,
  getCampaignsPromise,
} from "../services/apiService";
import LoadingOverlay from "../components/shared/LoadingOverlay";
import EmptyState from "../components/shared/EmptyState";
import DropdownMenu from "../components/shared/DropdownMenu";
import LeadListModal from "../components/leadLists/LeadListModal";
import useImportProgress from "../hooks/useImportProgress";
import { getErrorMessage, notifyEventError } from "../helpers/apiError";

// --- small presentational icons, scoped to this page ---
const SearchIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
    <path d="M17 17l-3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const ChevronDownIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 7.5l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const KebabIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor">
    <circle cx="10" cy="4" r="1.6" />
    <circle cx="10" cy="10" r="1.6" />
    <circle cx="10" cy="16" r="1.6" />
  </svg>
);

const PersonMiniIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="6.8" r="3.1" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3.8 17c0-3.3 2.9-5.4 6.2-5.4s6.2 2.1 6.2 5.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CampaignBadge = ({ count }) => {
  const { t } = useTranslation();
  if (!count) {
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-medium bg-white border border-gray-200 text-gray-500">
        {t("leadLists.notInCampaign")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-medium bg-white border border-green-200 text-green-700">
      {t("leadLists.inCampaigns", { count })}
    </span>
  );
};

const LeadLists = () => {
  const { t } = useTranslation();
  const { lng } = useParams();
  const navigate = useNavigate();
  usePageTitle("leadLists.title");

  const { showModal } = useModal();

  const [leadLists, setLeadLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("");
  const [listTypeFilter, setListTypeFilter] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  useEffect(() => {
    getCampaignsPromise({ limit: 100 })
      .then((res) => {
        const data = res.data;
        const list = Array.isArray(data) ? data : data?.data || [];
        setCampaigns(list);
      })
      .catch(() => {});
  }, []);

  const campaignOptions = useMemo(
    () => campaigns.map((c) => ({ value: c.id, label: c.name })),
    [campaigns]
  );

  const fetchLeadLists = async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const params = { page: pagination.page, limit: pagination.limit, ...filters };
      const response = await getLeadListsPromise(params);
      const { data, page, limit, total, totalPages, hasNext, hasPrev } = response.data;
      setLeadLists(data || []);
      setPagination({ page, limit, total, totalPages, hasNext, hasPrev });
    } catch (err) {
      if (!silent) {
        setError(getErrorMessage(err, t("leadLists.toast.error")));
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  // Live import progress via Socket.IO (no 1s HTTP poll → avoids rate-limit 429)
  useImportProgress((event) => {
    const listId = event.leadListId || event.leadId;
    if (!listId) return;
    setLeadLists((prev) =>
      prev.map((list) =>
        list.id === listId
          ? {
              ...list,
              importStatus: event.status || list.importStatus,
              importScraped: event.scraped ?? list.importScraped,
              importTarget: event.target ?? list.importTarget,
            }
          : list
      )
    );
    if (["COMPLETED", "FAILED", "CANCELLED"].includes(event.status)) {
      fetchLeadLists({ silent: true });
    }
    if (event.status === "FAILED") {
      notifyEventError(event, t("leads.toast.error"));
    }
  });

  const applyFilters = (overrides = {}) => {
    const nextSearch = overrides.search ?? searchValue;
    const nextCampaign = overrides.campaignId ?? campaignFilter;
    const nextListType = overrides.listType ?? listTypeFilter;
    setFilters({
      ...(nextSearch ? { search: nextSearch } : {}),
      ...(nextCampaign ? { campaignId: nextCampaign } : {}),
      ...(nextListType ? { listType: nextListType } : {}),
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Debounce free-text search so there's no separate "Filter" button
  useEffect(() => {
    const handle = setTimeout(() => {
      applyFilters({ search: searchValue });
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const openCreateModal = () => {
    showModal("leadLists.createTitle", <LeadListModal onSuccess={fetchLeadLists} />);
  };

  const openEditModal = (leadList) => {
    showModal("leadLists.editTitle", <LeadListModal leadList={leadList} onSuccess={fetchLeadLists} />);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("leadLists.deleteConfirm"))) return;
    const original = [...leadLists];
    setLeadLists(leadLists.filter((l) => l.id !== id));
    try {
      await deleteLeadListPromise(id);
      toast.success(t("leadLists.toast.deleteSuccess"));
    } catch {
      setLeadLists(original);
      toast.error(t("leadLists.toast.error"));
    }
  };

  const openList = (leadList) => {
    navigate(`/${lng}/leads/${leadList.id}`);
  };

  const startIndex = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endIndex = Math.min(pagination.page * pagination.limit, pagination.total);

  if (isLoading && leadLists.length === 0) return <LoadingOverlay />;

  return (
    <div className="py-[20px]">
      <h1 className="text-[26px] font-bold text-gray-900 mb-4">{t("leadLists.title")}</h1>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Card header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-3 flex-1">
            <div className="relative flex-1 min-w-[200px] max-w-[280px]">
              <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters({ search: searchValue })}
                placeholder={t("leadLists.searchPlaceholder", { defaultValue: "Search lists" })}
                className="w-full pl-10 pr-3 py-2.5 text-[14px] text-gray-700 bg-white border border-gray-200 rounded-lg outline-none focus:border-gray-400 transition-colors"
              />
            </div>

            <div className="relative">
              <select
                value={listTypeFilter}
                onChange={(e) => {
                  setListTypeFilter(e.target.value);
                  applyFilters({ listType: e.target.value });
                }}
                className="appearance-none pl-3.5 pr-9 py-2.5 text-[14px] font-semibold text-gray-800 bg-white border border-gray-200 rounded-lg outline-none focus:border-gray-400 min-w-[150px] cursor-pointer"
              >
                <option value="">{t("leadLists.listTypeAll", { defaultValue: "List type: All" })}</option>
                <option value="IN_CAMPAIGN">{t("leadLists.inCampaign", { defaultValue: "In campaign" })}</option>
                <option value="NOT_IN_CAMPAIGN">{t("leadLists.notInCampaign")}</option>
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>

            <div className="relative">
              <select
                value={campaignFilter}
                onChange={(e) => {
                  setCampaignFilter(e.target.value);
                  applyFilters({ campaignId: e.target.value });
                }}
                className="appearance-none pl-3.5 pr-9 py-2.5 text-[14px] text-gray-500 bg-white border border-gray-200 rounded-lg outline-none focus:border-gray-400 min-w-[160px] cursor-pointer"
              >
                <option value="">{t("leadLists.allCampaigns", { defaultValue: "All campaigns" })}</option>
                {campaignOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>

          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[14px] font-semibold px-4 py-2.5 rounded-lg transition-colors shrink-0"
          >
            <PlusIcon className="w-4 h-4" />
            {t("leadLists.addLeads", { defaultValue: "Add leads" })}
          </button>
        </div>

        {error && <div className="px-6 py-3 text-red-500 text-sm">{error}</div>}

        {leadLists.length === 0 ? (
          <div className="p-4">
            <EmptyState message="leadLists.noLists" cta="leadLists.create" onCtaClick={openCreateModal} />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[760px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-6 py-3 text-[12px] font-semibold text-gray-500">
                      {t("leadLists.name_col", { defaultValue: "List Name" })}
                    </th>
                    <th className="px-4 py-3 text-[12px] font-semibold text-gray-500">
                      {t("leadLists.status_col", { defaultValue: "Status" })}
                    </th>
                    <th className="px-4 py-3 text-[12px] font-semibold text-gray-500">{t("leadLists.leadCount_col")}</th>
                    <th className="px-4 py-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {leadLists.map((leadList) => (
                    <tr
                      key={leadList.id}
                      onClick={() => openList(leadList)}
                      className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4 min-w-[220px]">
                        <div className="flex items-center gap-3">
                          <span className="w-9 h-9 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center flex-shrink-0">
                            <PersonMiniIcon className="w-[18px] h-[18px]" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-[14px] font-semibold text-gray-900 truncate">{leadList.name}</p>
                            <p className="text-[12px] text-gray-400 truncate">
                              {leadList.createdAt
                                ? new Date(leadList.createdAt).toLocaleString([], {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                    hour: "numeric",
                                    minute: "2-digit",
                                    second: "2-digit",
                                  })
                                : leadList.description || ""}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        {leadList.importStatus === "IMPORTING" ? (
                          <div className="min-w-[140px]">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-medium bg-blue-50 border border-blue-200 text-blue-700 mb-1">
                              {t("leadLists.importingStatus")}
                            </span>
                            <div className="h-1.5 w-full rounded-full bg-blue-100 overflow-hidden">
                              <div
                                className="h-full bg-blue-600"
                                style={{
                                  width: `${
                                    leadList.importTarget > 0
                                      ? Math.min(
                                          100,
                                          Math.round(
                                            ((leadList.importScraped || 0) / leadList.importTarget) * 100
                                          )
                                        )
                                      : 0
                                  }%`,
                                }}
                              />
                            </div>
                            <p className="text-[11px] text-blue-600 mt-0.5">
                              {(leadList.importScraped || 0)}/{leadList.importTarget || 1000}
                            </p>
                          </div>
                        ) : (
                          <CampaignBadge count={leadList.campaignCount} />
                        )}
                      </td>

                      <td className="px-4 py-4">
                        <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-gray-600">
                          <PersonMiniIcon className="w-3.5 h-3.5 text-gray-400" />
                          {leadList.leadCount ?? 0}
                        </span>
                      </td>

                      <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <LeadListActionsDropdown
                          onEdit={() => openEditModal(leadList)}
                          onDelete={() => handleDelete(leadList.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
              <div className="text-[13px] text-gray-500">
                {t("campaign.showingPrefix", { defaultValue: "Showing" })}{" "}
                <strong className="text-gray-700 font-semibold">
                  {startIndex}-{endIndex}
                </strong>{" "}
                {t("campaign.showingOf", { defaultValue: "of" })}{" "}
                <strong className="text-gray-700 font-semibold">{pagination.total}</strong>
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={!pagination.hasPrev}
                  onClick={() => handlePageChange(pagination.page - 1)}
                  className="px-3.5 py-1.5 text-[13px] font-medium text-gray-600 bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                >
                  {t("pagination.previous")}
                </button>
                <button
                  disabled={!pagination.hasNext}
                  onClick={() => handlePageChange(pagination.page + 1)}
                  className="px-3.5 py-1.5 text-[13px] font-medium text-gray-600 bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                >
                  {t("pagination.next")}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const LeadListActionsDropdown = ({ onEdit, onDelete }) => {
  const { t } = useTranslation();

  return (
    <DropdownMenu trigger={<KebabIcon className="w-4 h-4" />} menuClassName="w-40">
      <button onClick={onEdit} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
        {t("leadLists.edit")}
      </button>
      <hr className="my-1" />
      <button onClick={onDelete} className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
        {t("leadLists.delete")}
      </button>
    </DropdownMenu>
  );
};

export default LeadLists;
